import React, { useEffect, useState, useCallback, useRef } from "react";
import FormInput from "./FormInput";
import SelectInput from "./SelectInput";
import CoordinatesInput from "./CoordinatesInput";
import { postSolution, editSolution, removeSolution, getSolutionStatus, getProfileSolutions } from "api/solutions";
import Modal from "components/Modal/ConfirmationModal";
import { IoMdArrowDropdown } from "react-icons/io";
import AddProjectGoogleMap from "../AddProjectGoogleMap";
import { projectTypeEnergy, projectTypeSmartCity, projectTypeCyberSecurity } from "@/lib/constants";
import SmartImg from "assets/Smart.jpg";
import EnergyImg from "assets/Energy.jpg"; // Fix the typo
import CyberImg from "assets/CyberImg.jpg";


const locationCoordinates: Record<string, [number, number]> = {
  "Al Bahah": [20.012, 41.467],
  "Al Jawf": [29.971, 39.573],
  "Al Hudud ash Shamaliyah": [30.000, 40.500],
  "Al Qasim": [26.325, 43.975],
  "Al Madinah": [24.468, 39.614],
  "Ash Sharqiyah (Eastern Province)": [26.500, 50.200],
  "Ar Riyad": [24.713, 46.675],
  "Jizan": [16.889, 42.561],
  "‘Asir": [18.240, 42.507],
  "Ha’il": [27.524, 41.690],
  "Makkah": [21.389, 39.857],
  "Tabuk": [28.383, 36.583],
  "Najran": [17.492, 44.127],
};


const industries = [
  { value: "ENERGY", label: "Energy", img: EnergyImg },
  { value: "SMARTCITY", label: "Smart City", img: SmartImg },
  { value: "CYBERSECURITY", label: "Cyber Security", img: CyberImg }
];


const NewProjectForm: React.FC<any> = ({
  setRefreshMode,
  setError,
  mode,
  formState,
  setFormState,
  setIsLoading,
  selectedOrganization,
  selectedProject,
  setNewProjectModal,
  selectedOption,
  goNext,
  onSaveSubmitFunction,
  setIsModalOpen,
  isModalOpen,
  setStep,
  activeTab = "operational"
}) => {
  console.log("formState in project form", formState);
  const [showMap, setShowMap] = useState(false);
  const [logo, setLogo] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const imageRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [industryValue, setIndustryValue] = useState<string>(formState.industry);
  // Initialize preview when formState.logoPreview is available
  useEffect(() => {
    if (formState.logoPreview) {
      setPreview(formState.logoPreview);
    }
  }, [formState.logoPreview]);

  // Initialize form data just once, then preserve the data
  useEffect(() => {
    // Only initialize if formState is empty
    const shouldInitialize = (
      mode === "edit" && selectedProject && (!formState.projectName || formState.projectName === "")
    ) || (
        mode === "create" && !formState.projectName && !formState.building
      );

    if (mode === "edit" && selectedProject) {
      if (mode === "edit" && selectedProject) {
        setFormState((prev: { [key: string]: any }) => ({
          projectName: selectedProject.name || "",
          productLocation: selectedProject.location || "",
          isPublic: selectedProject.isPublic || true,
          projectType: selectedProject.projectType || "",
          industry: selectedProject.industry || "",
          building: selectedProject.desc || "",
          desc: selectedProject.desc || "",
          coordinates: selectedProject.coordinates || [null, null],
          logoPreview: prev.logoPreview || null,
        }));

        setSelected(industries.find((industry) => industry.value === formState.industry) || null);

      } else if (mode === "create") {
        setFormState((prev: { [key: string]: any }) => ({
          ...prev,
          projectName: prev.projectName || "",
          productLocation: prev.productLocation || "",
          building: selectedOption || "",
          industry: selectedOption || "",
          desc: prev.desc || "",
          coordinates: prev.coordinates || [null, null],
        }));
      }
    }
  }, [mode, selectedProject, selectedOption, setFormState]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === "lat" || name === "lng") {
      const index = name === "lat" ? 0 : 1;
      const updatedCoordinates = [...formState.coordinates];
      updatedCoordinates[index] = value ? parseFloat(value) : null;
      setFormState((prev: { [key: string]: any }) => ({
        ...prev,
        coordinates: updatedCoordinates as [number | null, number | null],
      }));
    } else if (name === "productLocation") {
      setFormState((prev: { [key: string]: any }) => ({ ...prev, [name]: value }));
      if (locationCoordinates[value]) {
        setFormState((prev: { [key: string]: any }) => ({
          ...prev,
          coordinates: locationCoordinates[value],
        }));
      }
    } else if (name === "isPublic") {
      setFormState((prev: { [key: string]: any }) => ({ ...prev, [name]: value === "Public Project" ? true : false }));
    }
    else {
      setFormState((prev: { [key: string]: any }) => ({ ...prev, [name]: value }));
    }
  };

  // Create a memoized version of the submit handler that doesn't need the event parameter
  const submitForm = useCallback(async () => {
    setIsLoading(true);
    const preparedData = {
      name: formState.projectName,
      desc: formState.desc, // Send the description textarea value
      location: formState.productLocation,
      coordinates: JSON.stringify(formState.coordinates),
      industry: selectedOption || formState.industry ,
      category: activeTab || "operational",
      productId: activeTab,
      isPublic: formState.isPublic,
      projectType: formState.projectType,
      // building: selectedOption
    };

    try {
      let response;
      if (mode === "create") {
        response = await postSolution(selectedOrganization.id, preparedData);
        if (response?.status === 200) {
          const solutionId = response?.data?.value;
          // First API call
          await getSolutionStatus(selectedOrganization.id, solutionId);

          // Wait 2 seconds
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Second API call after delay
          const statusResponse = await getSolutionStatus(selectedOrganization.id, solutionId);
          if (statusResponse?.data?.data?.ready === true) {
            const solution = await getProfileSolutions(selectedOrganization.id);
            if (solution?.status === 200) {
              const solutionData = solution?.data?.list;
              const newSolution = solutionData?.find((solution: any) => solution.id === solutionId);
              if (newSolution) {
                window.open(newSolution?.url, '_blank');
              }
            }
          }
        }
      } else if (mode === "edit") {
        response = await editSolution(selectedOrganization.id, selectedProject.id, preparedData);
      }

      if (response?.status === 200 || response?.status === 204) {
        setRefreshMode("solutions");
        setNewProjectModal(false);
      } else {
        setError(response?.data);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setError("An unexpected error occurred.");
    }

    setIsLoading(false);
  }, [
    formState,
    selectedOption,
    mode,
    selectedOrganization,
    selectedProject,
    setError,
    setIsLoading,
    setNewProjectModal,
    setRefreshMode,
    activeTab
  ]);

  // Regular form submit handler that prevents default
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitForm();
  };

  // Save the submit function for external use once the dependencies are stable
  useEffect(() => {
    if (onSaveSubmitFunction) {
      onSaveSubmitFunction(submitForm);
    }
  }, [submitForm, onSaveSubmitFunction]);

  const closeModal = () => setIsModalOpen(false);

  const handleRemoveItem = async () => {
    try {
      const response = await removeSolution(selectedOrganization.id, selectedProject.id);
      if (response.status === 200 || response.status === 204) {
        setFormState({
          projectName: "",
          productLocation: "",
          isPublic: true,
          building: "",
          coordinates: [null, null] as [number | null, number | null],
        });
        setRefreshMode("solutions");
        setNewProjectModal(false);
        setStep(1);
      }
    } catch (error) {
      console.error("Error removing item:", error);
      setError("Failed to remove the project.");
    }
  };

  const confirmRemove = async () => {
    await handleRemoveItem();
    closeModal();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const name = e.target.name;
    const value = e.target.value;
    if (file) {
      setLogo(file);
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);
      setFormState((prev: { [key: string]: any }) => ({
        ...prev,
        [name]: value,
        logoPreview: previewUrl // Store the preview URL in formState
      }));
    }
  };

  const getProjectTypeOptions = (selectedOption: string) => {

    switch (selectedOption) {
      case "ENERGY":
        return projectTypeEnergy;
      case "SMARTCITY":
        return projectTypeSmartCity;
      case "CYBERSECURITY":
        return projectTypeCyberSecurity;
      default:
        return [];
    }
  };


  return (
    <form onSubmit={handleSubmit} className="h-full text-sm xl:text-md 2xl:text-lg">
      <div className="">
      
      <div className={`grid  gap-5 ${mode === "edit" ? "sm:grid-cols-2" : "grid-cols-1"}`}>
          <FormInput
            id="projectName"
            name="projectName"
            value={formState.projectName}
            placeholder="Project Name"
            onChange={handleInputChange}
          />
          {mode === "edit" && (
            <>
              <div className="relative w-full mb-5">
                <label htmlFor="desc" className="block text-sm text-gray-400 mb-2">
                  Select Industry
                </label>
                <button
                  className="w-full flex items-center justify-between bg-[#2A2A2A] text-white px-3 py-2 rounded-md border border-white/[0.08] text-sm"
                  type="button"
                  onClick={() => setOpen(!open)}
                >


                  {selected ? (
                    <div className="flex items-center gap-2">
                      <img src={selected.img} alt={selected.label} className="w-6 h-6 object-cover" />
                      {selected.label}
                    </div>
                  ) : formState.industry ? (
                    formState.industry
                  ) : (
                    "Select Industry"
                  )}
                  <span>▼</span>
                </button>

                {open && (
                  <div className="absolute z-10 bg-[#2A2A2A] w-full border border-white/[0.08] rounded-md mt-1">
                    {industries.map((industry) => (
                      <div
                        key={industry.value}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-[#444] cursor-pointer"
                        onClick={() => {
                          setSelected(industry);
                          setIndustryValue(industry.value);
                          setFormState((prev: { [key: string]: any }) => ({ ...prev, industry: industry.value }));
                          setOpen(false);
                        }}
                      >
                        <img src={industry.img} alt={industry.label} className="w-6 h-6 object-cover" />
                        {industry.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </>

          )}
        </div>


        <div className="w-full flex flex-wrap justify-between items-center mb-5">
          <label className="block text-sm text-gray-400 mb-2">Description</label>
          <textarea
            value={formState.desc}
            onChange={handleInputChange}
            placeholder="Description"
            name="desc"
            minLength={1}
            className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white resize-none"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <SelectInput
            id="isPublic"
            name="isPublic"
            label="Select Visibility"
            value={formState.isPublic ? "Public Project" : "Private Project"}
            options={[
              { value: "Public Project", label: "Public" },
              { value: "Private Project", label: "Private" }
            ]}
            onChange={handleInputChange}
          />

          <SelectInput
            id="productLocation"
            name="productLocation"
            label="Project Location"
            value={formState.productLocation}
            options={Object.keys(locationCoordinates).map((loc) => ({
              value: loc,
              label: loc,
            }))}
            onChange={handleInputChange}
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">

          {/* {activeTab !== "82000000-eaf8-488b-9993-12542d0dc61b" &&  */}
          <SelectInput
            id="projectType"
            name="projectType"
            label="Project Type"
            value={formState.projectType}
            options={getProjectTypeOptions(selectedOption || formState.industry)}
            onChange={handleInputChange}
          />
          {/* } */}
          {/* {activeTab !== "82000000-eaf8-488b-9993-12542d0dc61b" && */}
            <div className="mb-5 w-full">
              <label
                htmlFor="logo"
                className="block text-sm text-gray-400 mb-2"
              >
                Logo
              </label>
              <div className="flex items-center gap-2">
                <input accept="image/*" ref={imageRef} type="file" name="logo" id="logo" className="w-full  bg-[#2A2A2A] border border-white/[0.08] text-sm rounded-md px-3 py-2 text-white" onChange={handleFileChange} />
                {/* <input
                  type="text"
                  required
                  placeholder="Upload Logo"
                  readOnly
                  className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-[6.5px] text-md text-white"
                  onClick={handleLogoClick}
                /> */}
                {preview && (
                  <img src={preview} alt="Selected Logo" className=" h-10 w-10 rounded-md object-contain" />
                )}
              </div>
            </div>
          {/* } */}
        </div>


        {mode === "create" && (
          <div
            className="flex items-center gap-2 cursor-pointer mb-4"
            onClick={() => setShowMap(!showMap)}
          >
            <h5 className="text-sm text-[#F8585B]">Choose from map </h5>
            <IoMdArrowDropdown />
          </div>
        )}

        {showMap && mode === "create" && (
          <>
            <CoordinatesInput value={formState.coordinates} onChange={handleInputChange} />
            <div className="w-full overflow-hidden">
              <AddProjectGoogleMap
                height={"40vh"}
                initialCoordinates={formState.coordinates}
                onCoordinatesChange={(coords) => {
                  setFormState((prev: { [key: string]: any }) => ({
                    ...prev,
                    coordinates: coords
                  }));
                }}
              />
            </div>
            <br />
          </>
        )}

        {mode === "edit" && (
          <>
            <CoordinatesInput value={formState.coordinates} onChange={handleInputChange} />
            <div className="w-full overflow-hidden">
              <AddProjectGoogleMap
                height={"40vh"}
                initialCoordinates={formState.coordinates}
                onCoordinatesChange={(coords) => {
                  setFormState((prev: { [key: string]: any }) => ({
                    ...prev,
                    coordinates: coords
                  }));
                }}
              />
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        message="Are you sure you want to delete this item?"
        onConfirm={confirmRemove}
        onCancel={closeModal}
      />
    </form>
  );
};

export default NewProjectForm;
